import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/roles";
import { listIncidents } from "@/lib/incidents";
import {
  formatIncidentDateTime,
  INCIDENT_TYPE_META,
} from "@/lib/incidentTypes";
import {
  IncidentTypeBadge,
  SeverityBadge,
  StatusBadge,
} from "@/components/incidents/IncidentBadges";
import { getChildById } from "@/lib/dailyReportsDemo";

/**
 * Simple mobile-first list used by both /teacher/incidents and /parent/incidents.
 * Admin uses a separate table page.
 */
export default function IncidentsList({ basePath, role: rolePropOverride }) {
  const { profile, role: authRole } = useAuth();
  const role = rolePropOverride || authRole;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      try {
        const list = await listIncidents({ role, profileId: profile.id });
        setRows(list);
      } catch (err) {
        toast.error(err.message || "Could not load incidents");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id, role]);

  return (
    <div data-testid="incidents-list" className="space-y-5">
      <section className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Incident reports
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            {role === ROLES.PARENT ? "Your child's reports" : "Recent reports"}
          </h1>
        </div>
        {role === ROLES.TEACHER && (
          <Link
            to="/teacher/incidents/new"
            data-testid="new-incident-button"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover active:scale-[0.98]"
          >
            <Plus size={14} weight="bold" />
            New
          </Link>
        )}
      </section>

      {loading ? (
        <div
          data-testid="incidents-list-loading"
          className="flex items-center justify-center py-16"
        >
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="incidents-list-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500"
        >
          No incident reports on file.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="incidents-list-ul">
          {rows.map((r) => {
            const child =
              r.child || getChildById(r.child_id) || null;
            const childName = child
              ? `${child.first_name} ${child.last_name || ""}`.trim()
              : "Child";
            const typeMeta =
              INCIDENT_TYPE_META[r.incident_type] || INCIDENT_TYPE_META.other;
            const Icon = typeMeta.icon;
            return (
              <li key={r.id}>
                <Link
                  to={`${basePath}/${r.id}`}
                  data-testid={`incident-row-${r.id}`}
                  data-severity={r.severity}
                  data-status={r.status}
                  className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-sm"
                >
                  <div
                    className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${typeMeta.bg}`}
                  >
                    <Icon size={20} weight="duotone" className={typeMeta.color} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                      {childName}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-500">
                      {formatIncidentDateTime(r.occurred_at)}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <IncidentTypeBadge type={r.incident_type} />
                      <SeverityBadge severity={r.severity} />
                      <StatusBadge status={r.status} />
                    </div>
                  </div>
                  <CaretRight
                    size={18}
                    weight="bold"
                    className="mt-1 flex-shrink-0 text-neutral-400"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
